import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Send, Mail, Phone, MapPin, CheckCircle } from 'lucide-react';
import { contactInfo } from '../../data/mock';
import { toast } from 'sonner';

gsap.registerPlugin(ScrollTrigger);

const ContactSection = () => {
  const sectionRef = useRef(null);
  const titleRef = useRef(null);
  const formRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    playerType: 'jugador'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        titleRef.current,
        { y: 60, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 1,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 75%',
            toggleActions: 'play none none reverse'
          }
        }
      );

      gsap.fromTo(
        formRef.current,
        { y: 40, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 1,
          delay: 0.2,
          scrollTrigger: {
            trigger: formRef.current,
            start: 'top 85%',
            toggleActions: 'play none none reverse'
          }
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
      toast.success('¡Mensaje enviado! Nos pondremos en contacto pronto.');
      setFormData({ name: '', email: '', phone: '', message: '', playerType: 'jugador' });
      setTimeout(() => setIsSubmitted(false), 3000);
    }, 1500);
  };

  return (
    <section
      id="contacto"
      ref={sectionRef}
      className="relative py-16 lg:py-28 bg-black overflow-hidden"
      data-testid="contact-section"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-t from-white/[0.02] to-transparent" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
          {/* Left Column - Info */}
          <div ref={titleRef}>
            <span className="inline-block text-white/50 text-xs font-light tracking-[0.3em] uppercase mb-6">
              Contáctanos
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-white mb-4 leading-tight">
              Quiero ser{' '}
              <span className="text-white/50 font-medium">representado</span>
            </h2>
            <p className="text-white/60 text-base font-light mb-10 leading-relaxed">
              Si eres un jugador con talento o conoces a alguien que merece ser representado, contáctanos.
            </p>

            {/* Contact Info */}
            <div className="space-y-4">
              <a
                href={`mailto:${contactInfo.email}`}
                className="flex items-center gap-4 text-white/50 hover:text-white transition-colors duration-300 group"
                data-testid="contact-email"
              >
                <div className="w-11 h-11 bg-white/5 group-hover:bg-white/10 rounded-xl flex items-center justify-center transition-colors duration-300">
                  <Mail size={18} className="text-white/70" />
                </div>
                <div>
                  <p className="text-[10px] text-white/50 uppercase tracking-wider font-light">Email</p>
                  <p className="text-white/80 font-light">{contactInfo.email}</p>
                </div>
              </a>

              <a
                href={`tel:${contactInfo.phone}`}
                className="flex items-center gap-4 text-white/50 hover:text-white transition-colors duration-300 group"
                data-testid="contact-phone"
              >
                <div className="w-11 h-11 bg-white/5 group-hover:bg-white/10 rounded-xl flex items-center justify-center transition-colors duration-300">
                  <Phone size={18} className="text-white/70" />
                </div>
                <div>
                  <p className="text-[10px] text-white/30 uppercase tracking-wider font-light">Teléfono</p>
                  <p className="text-white/80 font-light">{contactInfo.phone}</p>
                </div>
              </a>

              <div className="flex items-center gap-4 text-white/50">
                <div className="w-11 h-11 bg-white/5 rounded-xl flex items-center justify-center">
                  <MapPin size={18} className="text-white/70" />
                </div>
                <div>
                  <p className="text-[10px] text-white/30 uppercase tracking-wider font-light">Ubicación</p>
                  <p className="text-white/80 font-light">{contactInfo.address}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Form */}
          <div ref={formRef}>
            <form
              onSubmit={handleSubmit}
              className="bg-white/5 rounded-2xl p-6 sm:p-8 border border-white/10"
              data-testid="contact-form"
            >
              <div className="space-y-5">
                {/* Player Type */}
                <div>
                  <label className="block text-white/80 text-sm font-light mb-3">Soy un...</label>
                  <div className="grid grid-cols-2 gap-3">
                    {['jugador', 'familiar'].map((type) => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => setFormData((prev) => ({ ...prev, playerType: type }))}
                        className={`px-4 py-3 rounded-xl font-light text-sm transition-all duration-300 ${
                          formData.playerType === type
                            ? 'bg-white text-black'
                            : 'bg-white/5 text-white/50 hover:bg-white/10 border border-white/10'
                        }`}
                        data-testid={`player-type-${type}`}
                      >
                        {type === 'jugador' ? 'Jugador' : 'Familiar/Rep.'}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Name */}
                <div>
                  <label className="block text-white/80 text-sm font-light mb-2">Nombre completo</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/20 focus:border-white/30 focus:ring-1 focus:ring-white/20 transition-all duration-300 outline-none font-light"
                    placeholder="Tu nombre"
                    data-testid="contact-name-input"
                  />
                </div>

                {/* Email & Phone */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white/80 text-sm font-light mb-2">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/20 focus:border-white/30 focus:ring-1 focus:ring-white/20 transition-all duration-300 outline-none font-light"
                      placeholder="tu@email.com"
                      data-testid="contact-email-input"
                    />
                  </div>
                  <div>
                    <label className="block text-white/80 text-sm font-light mb-2">Teléfono</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/20 focus:border-white/30 focus:ring-1 focus:ring-white/20 transition-all duration-300 outline-none font-light"
                      placeholder="+598 99 123 456"
                      data-testid="contact-phone-input"
                    />
                  </div>
                </div>

                {/* Message */}
                <div>
                  <label className="block text-white/80 text-sm font-light mb-2">Mensaje</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/20 focus:border-white/30 focus:ring-1 focus:ring-white/20 transition-all duration-300 outline-none resize-none font-light"
                    placeholder="Cuéntanos sobre ti o el jugador..."
                    data-testid="contact-message-input"
                  />
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full px-6 py-4 bg-white hover:bg-white/90 text-black font-medium rounded-xl transition-all duration-300 flex items-center justify-center gap-3 disabled:opacity-50"
                  data-testid="contact-submit-btn"
                >
                  {isSubmitting ? (
                    <><div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" /><span>Enviando...</span></>
                  ) : isSubmitted ? (
                    <><CheckCircle size={20} /><span>¡Enviado!</span></>
                  ) : (
                    <><Send size={20} /><span>Enviar mensaje</span></>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
